import { Input } from "@/components/ui/input";
import {
  Search,
  Check,
  ChevronsUpDown,
  SlidersHorizontal,
  X,
  Filter,
  FilterXIcon,
} from "lucide-react";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Wilayas } from "@/lib/wilayas";
import { useState } from "react";
import { cn } from "@/lib/utils";

const MachinesTableHeader = ({
  searchValue,
  onSearchChange,
  sizeValue,
  wilayaValue,
  sortValue,
  statusValue,
  onStatusChange,
  onSizeChange,
  onWilayaChange,
  onSortChange,
}) => {
  const [openWilaya, setOpenWilaya] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  return (
    <div className="space-y-4 mb-4">
      {/* Top bar: search + filter button */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Search machines..."
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>

        <Button
          variant="outline"
          size="icon"
          onClick={() => setShowFilters((prev) => !prev)}
          className={cn(
            "transition-all duration-300",
            showFilters ? "bg-primary text-white" : "bg-background"
          )}
        >
          {showFilters ? (
            <FilterXIcon className="h-5 w-5 transition-transform duration-300" />
          ) : (
            <Filter className="h-5 w-5 transition-transform duration-300" />
          )}
        </Button>
      </div>

      {/* Animated filter section */}
      <div
        className={cn(
          "grid grid-cols-1 md:grid-cols-4 gap-4 overflow-hidden transition-all duration-500 ease-in-out",
          showFilters
            ? "max-h-[500px] opacity-100 translate-y-0"
            : "max-h-0 opacity-0 -translate-y-2 pointer-events-none"
        )}
      >
        {/* Sort By */}
        <div className="w-full">
          <label className="block text-sm font-medium mb-1">Sort By</label>
          <Select
            value={sortValue || "created_at"}
            onValueChange={onSortChange}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Created at" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created_at">Creation Date</SelectItem>
              <SelectItem value="last_seen">Last Seen</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Status */}
        <div className="w-full">
          <label className="block text-sm font-medium mb-1">Status</label>
          <Select
            value={statusValue !== undefined ? statusValue : "all"}
            onValueChange={onStatusChange}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="true">Active</SelectItem>
              <SelectItem value="false">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Size */}
        <div className="w-full">
          <label className="block text-sm font-medium mb-1">Size</label>
          <Select value={sizeValue || "all"} onValueChange={onSizeChange}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="86">86</SelectItem>
              <SelectItem value="65">65</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Wilaya */}
        <div className="w-full">
          <label className="block text-sm font-medium mb-1">Wilaya</label>
          <Popover open={openWilaya} onOpenChange={setOpenWilaya}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={openWilaya}
                className="w-full justify-between"
              >
                {wilayaValue && wilayaValue !== "all"
                  ? Wilayas.find((wilaya) => wilaya === wilayaValue)
                  : "All"}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-0">
              <Command>
                <CommandInput placeholder="Search wilaya..." />
                <CommandList>
                  <CommandEmpty>No wilaya found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value="all"
                      onSelect={() => {
                        onWilayaChange("all");
                        setOpenWilaya(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          !wilayaValue || wilayaValue === "all"
                            ? "opacity-100"
                            : "opacity-0"
                        )}
                      />
                      All
                    </CommandItem>
                    {Wilayas.map((wilaya) => (
                      <CommandItem
                        key={wilaya}
                        value={wilaya}
                        onSelect={(currentValue) => {
                          onWilayaChange(
                            currentValue === wilayaValue ? "all" : currentValue
                          );
                          setOpenWilaya(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            wilayaValue === wilaya ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {wilaya}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  );
};

export default MachinesTableHeader;
